import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import client from "../lib/api";
import GooglePlacesInput from "./GooglePlacesInput";
import { SITUATION_OPTIONS, CONDITION_OPTIONS, TIMELINE_OPTIONS } from "../lib/situations";

const STEPS = ["Address", "Condition", "Situation", "Contact"];

// Extracted so we don't allocate a new object on every render — protects
// framer-motion's memoisation and any child React.memo boundaries.
const STEP_MOTION = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.2 },
};

export default function LeadFormMultiStep() {
  const [params] = useSearchParams();
  const nav = useNavigate();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    address: params.get("address") || "",
    city: params.get("city") || "",
    state: params.get("state") || "",
    zip: params.get("zip") || "",
    condition: "",
    timeline: "",
    situation: params.get("situation") || "",
    reason: "",
    name: "",
    phone: params.get("phone") || "",
    email: "",
    consent_transactional_sms: false,
    consent_marketing_sms: false,
    partial_id: params.get("partial_id") || null,
    source_page: params.get("source_page") || "home",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // save partial on step advance so we capture drop-offs
  useEffect(() => {
    if (form.partial_id) return; // already saved from hero
    if (!form.address && !form.phone && !form.email) return;
    (async () => {
      try {
        const { data } = await client.post("/leads/partial", {
          address: form.address,
          phone: form.phone,
          email: form.email,
          source_page: form.source_page,
        });
        setForm((f) => ({ ...f, partial_id: data.partial_id }));
      } catch (err) {
        // Non-fatal: partial capture is best-effort. Log so it's visible in
        // devtools/production monitoring but don't disrupt the user flow.
        console.warn("Partial lead capture failed:", err);
      }
    })();
  }, [step]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const canNext = () => {
    if (step === 0) return !!form.address.trim();
    if (step === 1) return !!form.condition && !!form.timeline;
    if (step === 2) return !!form.situation;
    if (step === 3) return !!form.name.trim() && form.phone.replace(/\D/g, "").length >= 10 && form.consent_transactional_sms;
    return false;
  };

  const submit = async () => {
    if (!canNext()) {
      if (!form.consent_transactional_sms) {
        setErr("Please agree to receive transaction-related text messages to continue.");
      }
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      const { data } = await client.post("/leads", form);
      nav(`/thank-you?score=${encodeURIComponent(data.ai_score || "Warm")}`);
    } catch (e) {
      setErr("We couldn't submit right now — please try again or call (614) 966-4283.");
      setSubmitting(false);
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="max-w-2xl mx-auto bg-white border border-[var(--border-soft)] rounded-lg p-6 sm:p-10 shadow-md" data-testid="multistep-form">
      <div className="mb-6">
        <div className="flex justify-between text-xs font-semibold uppercase tracking-wide text-muted mb-2">
          <span>Step {step + 1} of {STEPS.length}</span>
          <span>{STEPS[step]}</span>
        </div>
        <div className="h-2 bg-surface rounded-full overflow-hidden">
          <div
            className="h-full bg-money transition-[width] duration-300"
            style={{ width: `${progress}%` }}
            data-testid="progress-bar"
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={STEP_MOTION.initial}
          animate={STEP_MOTION.animate}
          exit={STEP_MOTION.exit}
          transition={STEP_MOTION.transition}
        >
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="text-2xl sm:text-3xl font-display text-navy">Where is the property?</h2>
              <GooglePlacesInput
                value={form.address}
                onChange={({ address, city, state, zip }) =>
                  update({
                    address,
                    ...(city !== undefined && { city }),
                    ...(state !== undefined && { state }),
                    ...(zip !== undefined && { zip }),
                  })
                }
                placeholder="Street address"
                testId="step-address-input"
              />
              <div className="grid grid-cols-3 gap-3">
                <input type="text" placeholder="City" value={form.city} onChange={(e)=>update({city:e.target.value})} className="h-12 px-3 border-2 border-[var(--border-soft)] rounded-md" data-testid="step-city-input" />
                <input type="text" placeholder="State" value={form.state} onChange={(e)=>update({state:e.target.value})} className="h-12 px-3 border-2 border-[var(--border-soft)] rounded-md" data-testid="step-state-input" />
                <input type="text" placeholder="Zip" value={form.zip} onChange={(e)=>update({zip:e.target.value})} className="h-12 px-3 border-2 border-[var(--border-soft)] rounded-md" data-testid="step-zip-input" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl font-display text-navy">Property condition & timeline</h2>
              <FieldGroup label="Condition">
                {CONDITION_OPTIONS.map((c) => (
                  <Option key={c} value={c} selected={form.condition === c} onClick={() => update({ condition: c })} testId={`condition-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
                ))}
              </FieldGroup>
              <FieldGroup label="Timeline">
                {TIMELINE_OPTIONS.map((c) => (
                  <Option key={c} value={c} selected={form.timeline === c} onClick={() => update({ timeline: c })} testId={`timeline-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
                ))}
              </FieldGroup>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl font-display text-navy">What's your situation?</h2>
              <FieldGroup label="Choose the best match">
                {SITUATION_OPTIONS.map((c) => (
                  <Option key={c} value={c} selected={form.situation === c} onClick={() => update({ situation: c })} testId={`situation-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
                ))}
              </FieldGroup>
              <div>
                <label className="block text-sm font-semibold uppercase tracking-wide text-navy-800 mb-2">Anything else we should know? (optional)</label>
                <textarea
                  value={form.reason}
                  onChange={(e) => update({ reason: e.target.value })}
                  rows={3}
                  className="w-full p-3 border-2 border-[var(--border-soft)] rounded-md focus:border-[var(--green-500)] focus:outline-none"
                  placeholder="e.g. Need to sell before the auction on the 15th"
                  data-testid="reason-input"
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-2xl sm:text-3xl font-display text-navy">Where do we send your offer?</h2>
              <input type="text" placeholder="Full name" value={form.name} onChange={(e)=>update({name:e.target.value})} className="w-full h-14 px-4 border-2 border-[var(--border-soft)] rounded-md text-lg" data-testid="step-name-input" />
              <input type="tel" placeholder="Phone" value={form.phone} onChange={(e)=>update({phone:e.target.value})} className="w-full h-14 px-4 border-2 border-[var(--border-soft)] rounded-md text-lg" data-testid="step-phone-input" />
              <input type="email" placeholder="Email (optional but helpful)" value={form.email} onChange={(e)=>update({email:e.target.value})} className="w-full h-14 px-4 border-2 border-[var(--border-soft)] rounded-md text-lg" data-testid="step-email-input" />
              <p className="text-xs text-muted">We'll never sell your info. We'll call within 24 hours with a firm cash offer.</p>

              <div className="pt-2 space-y-3" data-testid="step-consent-group">
                <label className="flex gap-3 items-start cursor-pointer" htmlFor="step-consent-transactional">
                  <input
                    id="step-consent-transactional"
                    type="checkbox"
                    checked={form.consent_transactional_sms}
                    onChange={(e) => update({ consent_transactional_sms: e.target.checked })}
                    className="mt-[3px] h-4 w-4 accent-[var(--green-500)] shrink-0 cursor-pointer"
                    data-testid="step-consent-transactional-checkbox"
                  />
                  <span className="text-[11px] sm:text-xs leading-snug text-navy-800">
                    I consent to receive non-marketing text messages from Aspire Collective Partners LLC regarding my property inquiry and transaction updates. Message frequency varies. Message & data rates may apply. Reply HELP for assistance, reply STOP to opt out.
                  </span>
                </label>
                <label className="flex gap-3 items-start cursor-pointer" htmlFor="step-consent-marketing">
                  <input
                    id="step-consent-marketing"
                    type="checkbox"
                    checked={form.consent_marketing_sms}
                    onChange={(e) => update({ consent_marketing_sms: e.target.checked })}
                    className="mt-[3px] h-4 w-4 accent-[var(--green-500)] shrink-0 cursor-pointer"
                    data-testid="step-consent-marketing-checkbox"
                  />
                  <span className="text-[11px] sm:text-xs leading-snug text-navy-800">
                    I consent to receive marketing text messages from Aspire Collective Partners LLC regarding promotions, offers, and real estate opportunities. Message frequency varies. Message & data rates may apply. Reply HELP for assistance, reply STOP to opt out.
                  </span>
                </label>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {err && <p className="text-sm text-red-600 mt-4" data-testid="form-error">{err}</p>}

      <div className="mt-8 flex items-center justify-between gap-3">
        <button
          type="button"
          disabled={step === 0}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          className="h-12 px-4 rounded-md border-2 border-navy text-navy font-semibold flex items-center gap-2 disabled:opacity-40"
          data-testid="step-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button
            type="button"
            disabled={!canNext()}
            onClick={() => setStep((s) => s + 1)}
            className="h-14 px-6 rounded-md bg-money text-white text-lg font-bold flex items-center gap-2 disabled:opacity-50"
            data-testid="step-next-btn"
          >
            Continue <ArrowRight size={18} />
          </button>
        ) : (
          <button
            type="button"
            disabled={!canNext() || submitting}
            onClick={submit}
            className="h-14 px-6 rounded-md bg-money text-white text-lg font-bold flex items-center gap-2 disabled:opacity-60"
            data-testid="step-submit-btn"
          >
            {submitting ? "Submitting…" : (<>Get My Cash Offer <Check size={18} /></>)}
          </button>
        )}
      </div>
      <p className="text-[11px] text-muted text-center mt-6" data-testid="step-legal-links">
        <Link to="/privacy" className="hover:text-money underline-offset-2 hover:underline" data-testid="step-privacy-link">Privacy Policy</Link>
        <span className="mx-2 text-muted/60">|</span>
        <Link to="/terms" className="hover:text-money underline-offset-2 hover:underline" data-testid="step-terms-link">Terms and Conditions</Link>
      </p>
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div>
      <div className="text-sm font-semibold uppercase tracking-wide text-navy-800 mb-3">{label}</div>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function Option({ value, selected, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-14 rounded-md border-2 text-left px-4 font-medium transition ${
        selected ? "border-[var(--green-500)] bg-[var(--green-500)]/5 text-navy" : "border-[var(--border-soft)] text-navy-800 hover:border-navy"
      }`}
      data-testid={testId}
    >
      {value}
    </button>
  );
}
